import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coffee, Plus, Edit2, Trash2, ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyMenuItem = {
  name: '',
  description: '',
  price: '',
  category: '',
  image_url: ''
};

export const AdminMenu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyMenuItem);
  const [saving, setSaving] = useState(false);

  const fetchMenuItems = useCallback(async () => {
    try {
      const response = await fetch(`${API}/menu`);
      const data = await response.json();
      setMenuItems(data);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuItems();
  }, [fetchMenuItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingItem 
        ? `${API}/admin/menu/${editingItem.item_id}`
        : `${API}/admin/menu`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price)
        })
      });

      if (!response.ok) throw new Error('Failed to save menu item');

      toast.success(editingItem ? 'Menu item updated!' : 'Menu item added!');
      setIsDialogOpen(false);
      setEditingItem(null);
      setFormData(emptyMenuItem);
      fetchMenuItems();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save menu item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this menu item?')) return;

    try {
      const response = await fetch(`${API}/admin/menu/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Menu item deleted!');
      fetchMenuItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete menu item');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      category: item.category,
      image_url: item.image_url || ''
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData(emptyMenuItem);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-menu">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#FF00FF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#FF00FF] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Coffee className="w-8 h-8 text-[#FF00FF]" />
            <h1 className="font-permanent-marker text-3xl text-black">Manage Menu</h1>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openAddDialog}
                className="btn-graffiti-pink flex items-center gap-2"
                data-testid="add-menu-btn"
              >
                <Plus size={20} /> Add Item
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-4 border-black rounded-xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bangers text-2xl">
                  {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="menu-form">
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="Tropical Sunrise"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="Mango, pineapple, and passion fruit blend..."
                    data-testid="input-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">Price *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="input-graffiti"
                      placeholder="6.99"
                      data-testid="input-price"
                    />
                  </div>
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">Category *</label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      className="input-graffiti"
                      placeholder="Smoothies"
                      data-testid="input-category"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Image</label>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-4 py-2 border-4 border-black rounded-lg font-bangers text-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-graffiti-pink flex items-center justify-center gap-2"
                    data-testid="save-menu-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    Save
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Menu Items List */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#FF00FF] animate-spin mx-auto" />
          </div>
        ) : menuItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <Coffee className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No menu items yet.</p>
              <p className="font-space-grotesk text-gray-500 mt-2">Add your first drink!</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.item_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-graffiti flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`menu-item-${index}`}
              >
                {item.image_url && (
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-black shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bangers text-xl text-black">{item.name}</h3>
                      <p className="font-space-grotesk text-gray-600 text-sm">{item.description}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-[#00FFFF] text-black text-xs font-space-grotesk font-semibold rounded border border-black">
                        {item.category}
                      </span>
                    </div>
                    <div className="bg-[#CCFF00] px-3 py-1 rounded-full border-2 border-black font-bangers text-lg shrink-0">
                      ${item.price.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openEditDialog(item)}
                    className="p-2 bg-[#CCFF00] rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`edit-menu-${index}`}
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.item_id)}
                    className="p-2 bg-red-500 text-white rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`delete-menu-${index}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminMenu;
